import axios from 'axios';
import { downloadReport, getDataFromServer } from '../../util';
import { flattenFraudTransactionHistory } from './fraudTransactionAdapter';
import { convertFilterRangeParamsForBackend } from '../../../util/timezone';

export class TmCommonModules {
  constructor(baseURL, httpHeaders, preventionBaseUrl) {
    this.baseURL = baseURL;
    this.httpHeaders = httpHeaders;
    this.preventionBaseUrl = preventionBaseUrl;
    this.cancelTokenSource = null;
  }

  cancelRequest = (message = 'Request canceled by user') => {
    if (this.cancelTokenSource) {
      this.cancelTokenSource.cancel(message);
    }
  };

  createCancelToken = () => {
    this.cancelRequest('New request initiated');
    this.cancelTokenSource = axios.CancelToken.source();
    return this.cancelTokenSource.token;
  };

  // Per-endpoint cancellation.
  //
  // `createCancelToken` above keeps ONE token source for the whole module, so every
  // call cancels whatever else is in flight — fine when only one request runs at a
  // time, wrong for endpoints that are fetched together. The Spatial timeline and
  // its scenario list are requested in the same effect, and the shared token made
  // the second call abort the first: the timeline died with a Cancel while the
  // dropdown loaded, surfacing as "Could not load the event timeline".
  //
  // Scoping by key keeps the useful half of the behaviour — a newer request for the
  // SAME endpoint still supersedes the older one, which is what makes rapid scenario
  // switching safe — without letting unrelated endpoints cancel each other.
  createScopedCancelToken = (key) => {
    if (!this.scopedCancelTokenSources) this.scopedCancelTokenSources = {};
    const previous = this.scopedCancelTokenSources[key];
    if (previous) previous.cancel('New request initiated');
    const source = axios.CancelToken.source();
    this.scopedCancelTokenSources[key] = source;
    return source.token;
  };

  // FIN-10470: callers build from/to in display/browser space (browser-local day
  // picks, or timeAdjustment-adjusted alert dates). Convert them to backend
  // (stored-instant) space here — exactly once per flow — so the txn list and
  // download windows match the displayed day. No-op without a timeAdjustment.
  fetchTMCommonTransactionHistoryAPI = async (payload) => {
    const { customerId, isSmartAssessment, ...params } = payload;
    const cancelToken = this.createCancelToken();
    // FIN-12659: fraud alerts leave the customer controller — alert-scoped
    // endpoint over the canonical view, no JSON party envelope in the URL.
    // The role-shaped response is flattened here so the shared TM_IAD column
    // renderers keep working. IAD/TM (and FRAML customer-scope mounts, which
    // fetch by a comma-joined alertId list) stay on the customer endpoint.
    if (params.isFraudAlertScope && params.alertId) {
      const { isFraudAlertScope, alertId, ...fraudParams } = params;
      const fraudUrl = `${this.baseURL}/fraudAlerts/${encodeURIComponent(
        alertId
      )}/transactionHistory`;
      const data = await getDataFromServer(
        fraudUrl,
        this.httpHeaders,
        fraudParams,
        cancelToken
      );
      return flattenFraudTransactionHistory(data);
    }
    // FIN-11941: customerId can be a JSON object key (e.g. fraud run-instance
    // {"sender_hashcode":...,"intermediary_hashcodes":[]}). It must be fully
    // percent-encoded — the browser leaves `[` `]` literal in the path, and the
    // Apache proxy fronting the API rejects unencoded `[`/`]` with a 400 (which,
    // lacking CORS headers, surfaces in the browser as a CORS preflight failure).
    // encodeURIComponent is a no-op for plain UUID customer ids.
    const url = `${this.baseURL}/customers/${encodeURIComponent(
      customerId
    )}/transactionHistory`;

    return await getDataFromServer(
      url,
      this.httpHeaders,
      convertFilterRangeParamsForBackend(params),
      cancelToken
    );
  };

  // FIN-11304 / BE-1 (FIN-11364) — FRAML-P alert event sequence for the Spatial
  // timeline. Route confirmed against the BE implementation:
  // UnifiedAlertsController `get("/:alertId/eventTimeline")`, registered as
  // ApiResource(Get, "/alerts/{alertId}/eventTimeline", …) in
  // CommonAaaResourcesHandler — so it is AAA-mapped and access filters apply.
  // `preventionBaseUrl` is AMLS_COMMON ('/common'), giving
  // /amls/common/alerts/{alertId}/eventTimeline.
  fetchFramlAlertEvents = async (payload) => {
    const { alertId, ...params } = payload;
    const cancelToken = this.createScopedCancelToken('framlAlertEvents');
    const url = `${this.preventionBaseUrl}/alerts/${encodeURIComponent(
      alertId
    )}/eventTimeline`;

    return await getDataFromServer(url, this.httpHeaders, params, cancelToken);
  };

  // FIN-11304 / FIN-12857 — the scenarios (triggered typologies) an alert fired
  // for, powering the timeline's scenario selector. Route confirmed against the BE:
  // UnifiedAlertsController `get("/:alertId/variants")`, AAA-registered as
  // ApiResource(Get, "/alerts/{alertId}/variants", …) — so the same access
  // filters as eventTimeline apply.
  //
  // THE PATH IS /variants, NOT /scenarios. It was renamed on the BE on
  // 2026-09-15 (7d13a65ce2) and the old name 404s. Only the URL moved — the
  // response is still AlertScenariosResponse, so the body below and every
  // consumer of it are unchanged, and the endpoint never existed on
  // prerelease/v6.4.0 under either name.
  //
  // Returns { alertId, scenarios: [{ typologyId, typologyName, eventCount }] }.
  // Deliberately a SEPARATE call from eventTimeline: the BE keeps them apart, and
  // the timeline must not wait on the selector to render.
  fetchFramlAlertScenarios = async (payload) => {
    const { alertId } = payload;
    const cancelToken = this.createScopedCancelToken('framlAlertScenarios');
    const url = `${this.preventionBaseUrl}/alerts/${encodeURIComponent(
      alertId
    )}/variants`;

    return await getDataFromServer(url, this.httpHeaders, {}, cancelToken);
  };

  // FIN-11304 / BE-3 — related cases for a FRAML-P alert. NOTE: route is
  // PROVISIONAL until the BE confirms the mount point (see BE-3 request).
  // `cm.alert` is 1 customer : 1 alert, so the BE side needs a cross-DB read of
  // fincense.unified_alerts — that is a design discussion, not just a ticket.
  fetchFramlRelatedCases = async (payload) => {
    const { alertId, ...params } = payload;
    const cancelToken = this.createCancelToken();
    const url = `${this.preventionBaseUrl}/alerts/${encodeURIComponent(
      alertId
    )}/relatedCases`;

    return await getDataFromServer(url, this.httpHeaders, params, cancelToken);
  };

  downloadTMCommonTransactionHistoryAPI = async (payload) => {
    const {
      customerId,
      customerIds,
      isSmartAssessment,
      downloadAll,
      ...rawParams } = payload;
    // FIN-10470: align the date window with the displayed day (see note above).
    const params = convertFilterRangeParamsForBackend(rawParams);
    let url = '';

    if (params.caseId && !customerId) {
      // Case-level download: caseId is always present, customerIds may be empty
      // string if alerts haven't loaded yet — BE validates and returns 400 if missing
      url = `${this.baseURL}/customers/caseTransactionHistory`;
      return await getDataFromServer(url, this.httpHeaders, {
        ...params,
        ...(customerIds && { customerIds })
      });
    } else {
      url = `${this.baseURL}/customers/${encodeURIComponent(
        customerId
      )}/transactionHistory`;
    }

    if (downloadAll) {
      return await getDataFromServer(url, this.httpHeaders, {
        ...params,
        customerId
      });
    }

    return await downloadReport(url, this.httpHeaders, {
      ...params
    });
  };

  fetchTMCommonTransactionTriggeredTypologiesAPI = async (payload) => {
    const { alertId, ...params } = payload;
    const url = `${this.baseURL}/alerts/${alertId}/triggeredTypologies`;
    return await getDataFromServer(url, this.httpHeaders, params);
  };

  fetchTMCommonScoreExplainablity = async (payload) => {
    const { alertId, ...params } = payload;
    const url = `${this.baseURL}/alerts/${alertId}/modelScoreExplainability`;
    return await getDataFromServer(url, this.httpHeaders, params);
  };

  fetchPreventionCommonScoreExplainablity = async (payload) => {
    const { alertId, ...params } = payload;
    const url = `${this.preventionBaseUrl}/alerts/${alertId}/scoreExplanation`;
    return await getDataFromServer(url, this.httpHeaders, params);
  };

  fetchAlertScoreExplanation = async (payload) => {
    const { alertId, ...params } = payload;
    const url = `${this.preventionBaseUrl}/alerts/${alertId}/alertScoreExplanation`;
    return await getDataFromServer(url, this.httpHeaders, params);
  };

  fetchTMCommonPeerAndProfileGraph = async (payload) => {
    const { alertId, ...params } = payload;
    const url = `${this.baseURL}/alerts/${alertId}/riskIndicatorGraph`;
    return await getDataFromServer(url, this.httpHeaders, params);
  };

  fetchTypologyList = async (payload) => {
    const { alertId, ...params } = payload;
    const url = `${this.baseURL}/alerts/${alertId}/triggeredTypologies`;
    return await getDataFromServer(url, this.httpHeaders, params);
  };

  fetchVariantsListForAlertId = async (payload) => {
    const { alertId, ...params } = payload;
    const url = `${this.baseURL}/alerts/${alertId}/triggeredRules`;
    return await getDataFromServer(url, this.httpHeaders, params);
  };

  fetchRiskIndicatorList = async (payload) => {
    const { alertId, typologyId, modelId, ...params } = payload;
    const url = `${this.baseURL}/alerts/${alertId}/riskIndicators?typologyId=${typologyId}&modelId=${modelId}`;
    return await getDataFromServer(url, this.httpHeaders, params);
  };

  fetchTMCommonCustomerLoanDetails = async (payload) => {
    const { customerId, ...params } = payload;
    const url = `${this.baseURL}/customers/${encodeURIComponent(
      customerId
    )}/loans`;
    return await getDataFromServer(url, this.httpHeaders, params);
  };

  fetchTransactionTypesFilterAPI = async (payload) => {
    const { partyKey, from, to } = payload;
    const url = `${this.baseURL}/customers/transactions/filters/transaction-types`;
    return await getDataFromServer(url, this.httpHeaders, {
      partyKey,
      from,
      to
    });
  };

  // FIN-11542 — Activity tab Advanced Filter option lists (activityTypes,
  // activityStatuses, channels). Replaces FE-hardcoded ACTIVITY_FILTER_FIELDS
  // in ActivityInfoFilters.js so option values flow through a real GET.
  // FIN-12168 — when an alert scope is supplied (alertId + from/to window +
  // optional customerId), the BE returns the DISTINCT facet values present in
  // that alert's activities (Channel from custom_data) instead of the static
  // canonical lists. Only defined keys are forwarded as query params.
  fetchActivityFilterOptionsAPI = async (payload = {}) => {
    const url = `${this.baseURL}/customers/activityFilterOptions`;
    const {
      alertId,
      customerId,
      from,
      to,
      isTriggeredOnly,
      typologyId,
      variantId
    } = payload;
    const params = {};
    if (alertId) params.alertId = alertId;
    if (customerId) params.customerId = customerId;
    if (from != null) params.from = from;
    if (to != null) params.to = to;
    // FIN-12168 — forward the live Triggered Only toggle so the BE re-scopes the
    // distinct facets (ON = triggered join; OFF = full party set minus triggered).
    if (isTriggeredOnly != null) params.isTriggeredOnly = isTriggeredOnly;
    // FIN-12168 — forward the selected typology/variant so the facet query mirrors
    // the same listing scope; without these the dropdowns showed values not present
    // in the (typology/variant-)filtered results. 'all' means no typology filter.
    if (typologyId && typologyId !== 'all') params.typologyId = typologyId;
    if (variantId && variantId !== 'all') params.variantId = variantId;
    return await getDataFromServer(url, this.httpHeaders, params);
  };

  fetchTMCommonActivityInformation = async (payload) => {
    const { customerId, isSmartAssessment, ...params } = payload;
    let url = '';

    if (isSmartAssessment) {
      url = `${this.baseURL}/customers/triggeredActivityHistory`;
      // FIN-11542/FIN-11305 — the triggeredActivityHistory route carries the
      // party key as a query param (no path segment). Without it, BE
      // findByAlertId falls through the `customerId.nonEmpty` guard and serves
      // the triggered-only JOIN, so "Triggered Only OFF" never widens to the
      // full in-window activity set. Re-include customerId here (it was stripped
      // by the destructure above for URL-building).
      return await getDataFromServer(url, this.httpHeaders, {
        ...params,
        ...(customerId && { customerId })
      });
    }

    url = `${this.baseURL}/customers/${encodeURIComponent(
      customerId
    )}/activityHistory`;
    return await getDataFromServer(url, this.httpHeaders, params);
  };

  downloadTMCommonActivityInformation = async (payload) => {
    const { customerId, isSmartAssessment, downloadAll, ...params } = payload;
    let url = '';

    // FIN-11542 — mirror the Transaction download flow exactly
    // (downloadTMCommonTransactionHistoryAPI above):
    //   • "Download All Matching" (downloadAll) → getDataFromServer: passes NO
    //     offset/limit, BE queues an async job and returns the job JSON to the
    //     saga (which shows the "Export In Progress / Ready" toast + the record
    //     surfaces in Download History).
    //   • "Download Current Page" → downloadReport: passes offset/limit, BE
    //     returns an immediate CSV that downloadReport saves as a file.
    // Do NOT default a limit — defaulting limit:5000 forced All-Matching down
    // the sync branch so it never queued. `downloadAll` is consumed here +
    // by the saga; it is NOT forwarded to the BE.
    if (isSmartAssessment) {
      url = `${this.baseURL}/customers/triggeredActivityHistory`;
      if (downloadAll) {
        return await getDataFromServer(url, this.httpHeaders, {
          ...params,
          ...(customerId && { customerId })
        });
      }
      return await downloadReport(url, this.httpHeaders, {
        ...params,
        ...(customerId && { customerId })
      });
    }

    url = `${this.baseURL}/customers/${encodeURIComponent(
      customerId
    )}/activityHistory`;
    if (downloadAll) {
      return await getDataFromServer(url, this.httpHeaders, { ...params });
    }
    return await downloadReport(url, this.httpHeaders, { ...params });
  };

  // FIN-11360 — FRAML View queue CSV download. Mirrors
  // downloadTMCommonTransactionHistoryAPI's current-page-vs-all split. BE
  // endpoint is AMLS-BE `/framl/queueExport` (tm base URL — same servlet
  // context as `/customers/.../transactionHistory`), NOT the CM-mounted
  // `/cm/framl/queue/export`.
  downloadFramlQueueExportAPI = async (payload) => {
    const { downloadAll, ...params } = payload;
    const url = `${this.baseURL}/framl/queueExport`;

    if (downloadAll) {
      return await getDataFromServer(url, this.httpHeaders, { ...params });
    }
    return await downloadReport(url, this.httpHeaders, {
      ...params,
      download: true
    });
  };
}
